import React from "react";
import * as UI from "./ui.jsx";
import { authorizedFetch } from "./boot.js";
import { useInspectionData } from "./inspection_display.js";
import { visiblePoll } from "./stats_shared.js";
import { InspectionsTab } from "./stats_inspections.jsx";
import { XrayLotPage, XrayLots } from "./stats_xray.jsx";
// Vendor Stats — two tabs for one sale: X-ray views and Inspections
// (docs/superpowers/specs/2026-09-24-stats-tabs-design.md).
//
// Inspections shows only for a caller who reads the inspection log and the lot
// counts, and whose vendor shows inspection figures; anyone else sees X-ray views
// alone and no tab bar. The choice is kept in the address as view=inspections.
//
// The X-ray tab's snapshot arrives from /ui/repository-stats@1, which holds the
// machine-to-machine credential server-side. Per the Repository integration guide a
// 200 is a complete replacement snapshot, lots are keyed by lotId with lotNumber as
// the display value, and a failed poll keeps the last good snapshot and marks it
// stale rather than showing zero activity. XrayTab owns that poll and the states
// around it; the list and lot page render what it holds.

const {
  useState: useStateSt,
  useEffect: useEffectSt,
  useRef: useRefSt,
} = React;

function StatsView() {
  const inspections = window.VD.inspections || {};
  const realm = (window.VD.access || {}).realm || "";
  const shown = useInspectionData();
  const inspectionsTab = Boolean(inspections.enabled && inspections.lotCounts && shown);
  const [view, setView] = useStateSt(() =>
    new URLSearchParams(window.location.search).get("view") === "inspections" ? "inspections" : "xray");
  const current = inspectionsTab && view === "inspections" ? "inspections" : "xray";
  const choose = (next) => {
    setView(next);
    // The app reads `tab` only when it starts, so both are written: a reload
    // returns to this tab of Stats rather than to wherever the app opened.
    const url = new URL(window.location.href);
    url.searchParams.set("tab", "stats");
    if (next === "inspections") url.searchParams.set("view", "inspections");
    else url.searchParams.delete("view");
    window.history.replaceState({}, "", url.toString());
  };
  const tab = (id, label) => (
    <button type="button" role="tab" aria-selected={current === id}
            className={current === id ? "on" : ""} onClick={() => choose(id)}>{label}</button>
  );
  return (
    <div className="stats-tabs">
      {inspectionsTab && (
        <div className="seg" role="tablist" aria-label="Stats">
          {tab("xray", "X-ray views")}{tab("inspections", "Inspections")}
        </div>
      )}
      {current === "inspections"
        ? <InspectionsTab key={realm} sale={inspections.sale || "26R2R"} vendor={realm}/>
        : <XrayTab/>}
    </div>
  );
}

// An Inglis administrator's Stats can show one vendor's X-ray views exactly as that
// vendor's own page shows them (owner, 27 Sep 2026): the route answers the chosen
// vendor's reviewed projection, and the same list and lot page render it. The
// choice is kept in the address as vendor=… so a reload returns to it.
function XrayTab() {
  const realm = (window.VD.access || {}).realm || "";
  const inglis = realm === "inglis";
  const cfg = window.VD.repositoryStats || {};
  const [vendor, setVendor] = useStateSt(() =>
    (inglis && new URLSearchParams(window.location.search).get("vendor")) || "");
  const [vendors, setVendors] = useStateSt([]);

  useEffectSt(() => {
    if (!inglis) return undefined;
    let live = true;
    const params = new URLSearchParams({ sale: cfg.saleId || "26R2R", limit: "100" });
    authorizedFetch(`/collections/admin.vendors.list@1/?${params}`)
      .then((res) => (res.ok ? res.json() : { items: [] }))
      .then((body) => { if (live) setVendors(Array.isArray(body.items) ? body.items : []); })
      .catch(() => { if (live) setVendors([]); });
    return () => { live = false; };
  }, [inglis, cfg.saleId]);

  const choose = (next) => {
    setVendor(next);
    const url = new URL(window.location.href);
    url.searchParams.set("tab", "stats");
    if (next) url.searchParams.set("vendor", next); else url.searchParams.delete("vendor");
    window.history.replaceState({}, "", url.toString());
  };

  return (
    <>
      {inglis && (
        <label className="admin-standing-pick stats-vendor-pick">
          <span className="qstat-label">Vendor</span>
          <select value={vendor} onChange={(event) => choose(event.target.value)} aria-label="Vendor">
            <option value="">Whole sale</option>
            {vendor && !vendors.some((item) => item.id === vendor) && <option value={vendor}>{vendor}</option>}
            {vendors.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
          </select>
        </label>
      )}
      <XraySnapshot key={vendor} vendor={vendor}/>
    </>
  );
}

function XraySnapshot({ vendor = "" }) {
  const { Tag } = UI;
  const cfg = window.VD.repositoryStats || {};
  const realm = (window.VD.access || {}).realm || "";
  const [snap, setSnap] = useStateSt(null);
  const [loading, setLoading] = useStateSt(true);
  const [selectedLot, setSelectedLot] = useStateSt(null);
  // The snapshot's entity tag, held across renders. The response is no-store,
  // so nothing else remembers it for us.
  const etagRef = useRefSt(null);

  const saleId = (snap && snap.saleId) || cfg.saleId || "";

  // A realm switch is a different vendor's lots; a lot page left open would
  // read another vendor's lot id against this one.
  useEffectSt(() => { setSelectedLot(null); }, [realm]);

  useEffectSt(() => {
    let cancelled = false;
    // A realm switch is a different resource; never carry a tag across it.
    etagRef.current = null;
    const load = async () => {
      try {
        // The route is gated like every other, and this poll outlives the
        // access token that started it, so each attempt carries a fresh one.
        //
        // The validator is sent back so a poll that finds nothing new answers
        // 304 before the host runs its projection. The response is no-store, so
        // the tag is held here rather than by the browser cache.
        const res = await authorizedFetch(
          vendor ? `/ui/repository-stats@1?${new URLSearchParams({ vendor })}` : "/ui/repository-stats@1",
          etagRef.current ? { headers: { "If-None-Match": etagRef.current } } : {},
        );
        if (res.status === 304) {
          // Nothing moved. Keep what is on screen rather than re-rendering it.
          if (!cancelled) setLoading(false);
          return;
        }
        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
          // Drop the validator: a recovered error must not replay a tag for a
          // body this view never accepted.
          etagRef.current = null;
          // An error body is a bounded server code, never a snapshot. Do not
          // fall through to the table: a masqueraded vendor must not see an
          // undefined sale or an empty-looking success state.
          const candidate = typeof body?.error === "string"
            ? body.error
            : body?.error?.code || body?.errors?.[0]?.code;
          const reason = typeof candidate === "string" && /^[A-Za-z0-9_.-]{1,64}$/.test(candidate)
            ? candidate
            : `HTTP_${res.status}`;
          const providerSource = body && typeof body === "object" ? body.source : null;
          if (!cancelled) {
            if (res.status !== 401 && res.status !== 403 && providerSource === "unconfigured") {
              const missing = Array.isArray(body.missing)
                ? body.missing.filter((item) => typeof item === "string" && /^[A-Za-z0-9_.-]{1,64}$/.test(item))
                : [];
              setSnap({ source: "unconfigured", saleId: cfg.saleId || body.saleId || null, missing });
            } else if (res.status !== 401 && res.status !== 403 && providerSource === "error") {
              const providerReason = typeof body.reason === "string" && /^[A-Za-z0-9_.-]{1,64}$/.test(body.reason)
                ? body.reason
                : reason;
              setSnap({ source: "error", reason: providerReason, saleId: cfg.saleId || body.saleId || null, status: res.status });
            } else {
              setSnap({ source: "error", reason: res.status === 401 || res.status === 403 ? "ACCESS_DENIED" : reason, saleId: cfg.saleId || null, status: res.status });
            }
            setLoading(false);
          }
          return;
        }
        // Read through a guard: an absent validator only costs one skipped
        // 304, while letting this throw would blank the whole Stats view over
        // an optional header.
        const tag = res.headers && res.headers.get("ETag");
        if (tag) etagRef.current = tag;
        if (!cancelled) { setSnap(body); setLoading(false); }
      } catch (e) {
        if (!cancelled) { setSnap({ source: "error", reason: "BROWSER_FETCH_FAILED" }); setLoading(false); }
      }
    };
    load();
    const every = Math.max(2, cfg.pollSeconds || 300) * 1000;
    const stop = visiblePoll(load, every);
    return () => { cancelled = true; stop(); };
  }, [cfg.pollSeconds, realm, vendor]);

  if (loading) {
    return <div className="fade-in modulegate"><div className="eyebrow">Vendor stats</div><div className="display" style={{ marginTop: 6 }}>Loading snapshot…</div></div>;
  }

  // Nothing configured: say exactly what is missing rather than showing an empty table.
  if (!snap || snap.source === "unconfigured") {
    return (
      <div className="fade-in modulegate">
        <div className="eyebrow">No credential configured</div>
        <div className="display" style={{ marginTop: 6 }}>Repository stats</div>
        <p>
          The Repository viewing-statistics pull needs its Auth0 machine-to-machine
          credential. Vendor Dashboard owns that client ID and secret; they are read from
          the environment at run time and never stored in this repository.
        </p>
        {(cfg.missing || []).length > 0 && (
          <p className="caption" style={{ marginTop: 10 }}>
            Not set: {(cfg.missing || []).map(m => <span key={m} className="mono">{m} </span>)}
          </p>
        )}
      </div>
    );
  }

  if (snap.source === "error") {
    return (
      <div className="fade-in modulegate">
        <div className="eyebrow">Snapshot unavailable</div>
        <div className="display" style={{ marginTop: 6 }}>Repository stats</div>
        <p>
          No snapshot has been read yet for sale <span className="mono">{snap.saleId}</span>.
          Reason <span className="mono">{snap.reason}</span>{snap.status ? <> · HTTP <span className="mono">{snap.status}</span></> : null}.
        </p>
        <p className="caption" style={{ marginTop: 10 }}>
          {snap.status === 403
            ? "A valid token whose subject is not the approved Vendor Dashboard subject returns 403."
            : snap.status === 404
              ? "Confirm the Inglis sale code exists in Repository."
              : "Transport, 429 and 5xx faults retry with bounded backoff; 400/401/403/404 do not."}
        </p>
      </div>
    );
  }

  const lot = selectedLot && (snap.lots || []).find((row) => String(row.lotId) === selectedLot);

  return (
    <div className="fade-in">
      {snap.stale && (
        <div className="card" style={{ borderColor: "var(--race-gold)", background: "#FBF6EA", marginBottom: 18 }}>
          <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
            <Tag variant="warn">Stale</Tag>
            <span style={{ fontSize: 14 }}>
              The last poll failed (<span className="mono">{snap.reason}</span>
              {snap.status ? <> · HTTP {snap.status}</> : null}). Showing the last good snapshot
              rather than zeroes.
            </span>
          </div>
        </div>
      )}

      {selectedLot
        ? (
          <XrayLotPage
            key={selectedLot}
            saleId={saleId}
            lot={lot || { lotId: selectedLot, lotNumber: selectedLot }}
            counted={snap.viewCounts?.lots?.[selectedLot]}
            horse={snap.horses?.[selectedLot]}
            onBack={() => setSelectedLot(null)}
          />
        )
        : <XrayLots snap={snap} onOpen={setSelectedLot}/>}
    </div>
  );
}

export { StatsView };
